import { calculateTotal } from '@containers/leads/quotationTool/constants';

export const QUOTATION_GST_RATE = 0.18;

const toFiniteNumber = (value: unknown) => {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? numericValue : 0;
};

/** Whole-rupee ceiling used for quotation line prices, builder totals, and aggregates. */
export const ceilQuotationAmount = (value: unknown) => Math.ceil(toFiniteNumber(value));

export const isQuotationProductActive = (product?: Record<string, any> | null) => product?.active !== false;

export const getQuotationProductGrossTotal = (
	product?: Record<string, any> | null,
	options: { includeInactive?: boolean; preferStoredPrice?: boolean } = {}
) => {
	if (!product) return 0;
	if (!options.includeInactive && !isQuotationProductActive(product)) return 0;
	// Saved quotation prices already include the server's allocation and line-rounding policy.
	if (options.preferStoredPrice && product.price != null && Number.isFinite(Number(product.price))) {
		return Math.round(Number(product.price));
	}
	const priceAdjustment = product.grossUpPriceAdjustment ?? product.gross_up_price_adjustment;
	if (priceAdjustment?.applied === true && product.price != null) return Math.round(toFiniteNumber(product.price));

	const shouldUseStoredPrice =
		product.builder || (options.preferStoredPrice && product.price !== null && product.price !== undefined);
	const rawTotal = shouldUseStoredPrice ? product.price || 0 : calculateTotal(product);
	// Ceiling each line so area subtotals and grand total always sum from the same whole-rupee amounts.
	return ceilQuotationAmount(rawTotal);
};

export const getQuotationProductsGrossTotal = (
	products: Array<Record<string, any>> = [],
	options: { includeInactive?: boolean; extras?: boolean | null; preferStoredPrice?: boolean } = {}
) =>
	products.reduce((sum, product) => {
		if (options.extras !== undefined && options.extras !== null && Boolean(product?.extras) !== options.extras) {
			return sum;
		}
		return sum + getQuotationProductGrossTotal(product, options);
	}, 0);

export const getQuotationTaxBreakup = (grossTotal: number, gstRate = QUOTATION_GST_RATE) => {
	const gross = ceilQuotationAmount(grossTotal);
	const netCost = Math.round(gross / (1 + gstRate));
	const gstAmount = gross - netCost;

	return {
		netCost,
		netTotal: netCost,
		gstAmount,
		grossTotal: gross
	};
};

export const buildQuotationGstLedger = (
	grossTotal: number,
	ledgerData?: Record<string, any> | null,
	gstRate = QUOTATION_GST_RATE
) => {
	const gross = ceilQuotationAmount(grossTotal);
	if (!gross) return ledgerData;

	const { netCost, gstAmount } = getQuotationTaxBreakup(gross, gstRate);
	const cgstAmount = Math.round(gstAmount / 2);
	const sgstAmount = gstAmount - cgstAmount;
	const discountAmount = toFiniteNumber(ledgerData?.discountAmt ?? ledgerData?.discount_amt);
	const payable = Math.max(0, gross - discountAmount);

	return {
		...(ledgerData || {}),
		quotationExGst: netCost,
		quotation_ex_gst: netCost,
		cgstAmount,
		cgst_amount: cgstAmount,
		sgstAmount,
		sgst_amount: sgstAmount,
		gstAmount,
		gst_amount: gstAmount,
		quotationInclGst: gross,
		quotation_incl_gst: gross,
		quotationTotal: gross,
		quotation_total: gross,
		inclusiveAfterCash: gross,
		inclusive_after_cash: gross,
		payable,
		total: payable
	};
};
